import { initMonitoring } from '@nucleus-commerce/admin-kit/monitoring';
import { configureApiClient } from '@nucleus-commerce/api-client/transport';

import { captureInstallPrompt } from '@/features/push-notifications';
import { session } from '@/shared/session/session';

import { config } from './config';
import { TWO_FACTOR_SETUP_PATH } from './guards';
import { queryClient } from './query-client';
import { router } from './router';

/** Routes a 401 must not bounce away from: the portal landing and the public account pages. */
const PUBLIC_PREFIXES = [
  '/login',
  '/accept-invite',
  '/forgot-password',
  '/reset-password',
  '/apply',
];
const isPublicPath = (pathname: string) =>
  pathname === '/' || PUBLIC_PREFIXES.some((prefix) => pathname.startsWith(prefix));

/** Wires transport, monitoring and (in mock mode) MSW before the first render. */
export async function boot(): Promise<void> {
  captureInstallPrompt();
  initMonitoring({
    dsn: config.monitoringDsn,
    environment: config.env,
    release: config.release,
    surface: 'admin',
  });

  configureApiClient({
    baseURL: config.apiBaseUrl,
    surface: 'admin',
    hooks: {
      // The organisation turned the requirement on mid-session: /me now says so, and the guard
      // on the refreshed session sends the user to the setup screen.
      onError: (error) => {
        if (error.code !== 'TWO_FACTOR_REQUIRED') return;
        if (window.location.pathname === TWO_FACTOR_SETUP_PATH) return;
        void session
          .refreshSession(queryClient)
          .finally(() => void router.navigate({ to: TWO_FACTOR_SETUP_PATH }));
      },
      onUnauthorized: (error) => {
        session.clearSession(queryClient);
        const { pathname, search } = window.location;
        // A support session that ended is not the operator's own login; there is nothing to return to.
        if (error.code === 'SUPPORT_SESSION_ENDED') {
          void router.navigate({ to: '/login', search: { notice: 'support_session_ended' } });
          return;
        }
        if (!isPublicPath(pathname)) {
          void router.navigate({ to: '/login', search: { redirect: `${pathname}${search}` } });
        }
      },
    },
  });

  if (config.apiMode === 'mock') {
    const { startMockWorker } = await import('@/mocks/browser');
    await startMockWorker();
  }
}
